import { useState, useCallback, useRef, useEffect } from 'react';
import gun from '../config/gun.config';
import { CallState, CallSignal } from '../types/chat';

const TURN_HOST = process.env.REACT_APP_TURN_HOST || '';
const TURN_PORT = process.env.REACT_APP_TURN_PORT || '3478';
const TURN_USERNAME = process.env.REACT_APP_TURN_USERNAME || '';
const TURN_CREDENTIAL = process.env.REACT_APP_TURN_CREDENTIAL || '';

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  ...(TURN_HOST ? [
    { urls: `stun:${TURN_HOST}:${TURN_PORT}` } as RTCIceServer,
    {
      urls: [
        `turn:${TURN_HOST}:${TURN_PORT}`,
        `turn:${TURN_HOST}:${TURN_PORT}?transport=tcp`,
      ],
      username: TURN_USERNAME,
      credential: TURN_CREDENTIAL,
    } as RTCIceServer,
  ] : []),
];

const CALL_TIMEOUT_MS = 30000;
const RING_TIMEOUT_MS = 30000;

function makeRoomId(a: string, b: string) {
  return [a, b].sort().join('__call__');
}

/** Derive the DM chat thread key (same formula as useChat.ts) */
function dmThreadKey(a: string, b: string) {
  return [a, b].sort().join('__');
}

/** Write a system message directly to a DM chat thread */
function writeSystemMessage(threadKey: string, text: string) {
  gun.get('stabeon_chat').get(threadKey).set({
    senderId: 'system',
    senderNick: 'System',
    text,
    timestamp: Date.now(),
    messageType: 'system',
  } as any);
}

export function useVideoCall(myId: string, myNick: string) {
  const [callState, setCallState] = useState<CallState>('idle');
  const [roomId, setRoomId] = useState(''); // reactive mirror of room.current for useEffect deps
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(true); // default: speaker on
  const [incomingCall, setIncomingCall] = useState<CallSignal | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callPeerNick, setCallPeerNick] = useState('');
  const [callError, setCallError] = useState<string | null>(null);

  const localStreamRef  = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  // Hidden <audio> element used for speaker routing
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  const pc              = useRef<RTCPeerConnection | null>(null);
  const room            = useRef('');
  const peer            = useRef('');
  const peerNickRef     = useRef('');
  const timer           = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeout         = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ringTimeout     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iceTimeout      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const state           = useRef<CallState>('idle');
  const iceBuf          = useRef<RTCIceCandidateInit[]>([]);
  const seen            = useRef(new Set<string>());
  // Timestamp when last cleanup ran — signals older than this are ignored on reconnect
  const cleanedUpAt     = useRef<number>(0);

  useEffect(() => { state.current = callState; }, [callState]);

  const setLocalStreamBoth = useCallback((s: MediaStream | null) => {
    localStreamRef.current = s;
    setLocalStream(s);
  }, []);

  const setRemoteStreamBoth = useCallback((s: MediaStream | null) => {
    remoteStreamRef.current = s;
    setRemoteStream(s);
    // Wire remote stream to hidden audio element for speaker routing
    if (audioElRef.current) {
      audioElRef.current.srcObject = s;
      if (s) audioElRef.current.play().catch(() => {});
    }
  }, []);

  // Create (or reuse) the hidden audio element for speaker routing
  useEffect(() => {
    if (!audioElRef.current) {
      const el = document.createElement('audio') as any;
      el.autoplay = true;
      el.playsInline = true; // iOS hint
      el.style.display = 'none';
      document.body.appendChild(el);
      audioElRef.current = el;
    }
    return () => {
      if (audioElRef.current) {
        audioElRef.current.srcObject = null;
        audioElRef.current.remove();
        audioElRef.current = null;
      }
    };
  }, []);

  // Progressive media fallbacks
  async function getMedia(video: boolean): Promise<MediaStream> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Camera/microphone not supported in this browser.');
    }
    try { return await navigator.mediaDevices.getUserMedia({ audio: true, video }); } catch {}
    try { return await navigator.mediaDevices.getUserMedia({ audio: true, video: false }); } catch {}
    try { return await navigator.mediaDevices.getUserMedia({ audio: true }); } catch (e: any) {
      const msg = e?.name === 'NotAllowedError'
        ? 'Microphone access was denied. Please allow it in your device settings.'
        : e?.name === 'NotFoundError'
        ? 'No microphone found on this device.'
        : 'Could not access camera or microphone.';
      throw new Error(msg);
    }
  }

  const cleanup = useCallback((writeMissed = false) => {
    if (state.current === 'idle' && !pc.current) return;
    console.log('[Call] Cleanup, writeMissed=', writeMissed);

    // Write missed call system message if caller hung up / timed out
    if (writeMissed && peer.current && myId) {
      const tKey = dmThreadKey(myId, peer.current);
      const nick = peerNickRef.current;
      writeSystemMessage(tKey, `📞 Missed call${nick ? ` from ${nick}` : ''}`);
    }

    // Write tombstone so stale GUN signals are ignored after page reload
    cleanedUpAt.current = Date.now();
    if (myId) {
      gun.get('jrtc').get(myId).get(`_tomb_${cleanedUpAt.current}`).put({
        _tombstone: true, ts: cleanedUpAt.current,
      } as any);
    }

    localStreamRef.current?.getTracks().forEach(t => t.stop());
    remoteStreamRef.current?.getTracks().forEach(t => t.stop());
    pc.current?.close(); pc.current = null;
    if (timer.current)           { clearInterval(timer.current);           timer.current = null; }
    if (timeout.current)         { clearTimeout(timeout.current);          timeout.current = null; }
    if (ringTimeout.current)     { clearTimeout(ringTimeout.current);      ringTimeout.current = null; }
    if (iceTimeout.current)      { clearTimeout(iceTimeout.current);       iceTimeout.current = null; }
    if (disconnectTimer.current) { clearTimeout(disconnectTimer.current);  disconnectTimer.current = null; }
    iceBuf.current = [];
    seen.current   = new Set();
    setLocalStreamBoth(null);
    setRemoteStreamBoth(null);
    setCallState('idle');
    setCallDuration(0);
    setIsMuted(false);
    setIsVideoOff(false);
    setIncomingCall(null);
    setCallPeerNick('');
    setCallError(null);
    room.current = '';
    peer.current = '';
    peerNickRef.current = '';
    setRoomId('');
  }, [myId, setLocalStreamBoth, setRemoteStreamBoth]);

  const createPC = useCallback((stream: MediaStream): RTCPeerConnection => {
    const conn = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    stream.getTracks().forEach(t => {
      t.enabled = true;
      conn.addTrack(t, stream);
      console.log('[Call] Added track:', t.kind);
    });

    conn.ontrack = e => {
      console.log('[Call] Remote track:', e.track.kind);
      if (e.streams[0]) setRemoteStreamBoth(e.streams[0]);
    };

    conn.onicecandidate = e => {
      if (e.candidate && room.current) {
        const sig = `ice_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        gun.get('jrtc').get(room.current).get(sig).put({
          f: myId, t: 'ice', d: JSON.stringify(e.candidate.toJSON()), ts: Date.now(),
        } as any);
      }
    };

    conn.onconnectionstatechange = () => {
      console.log('[Call] Connection state:', conn.connectionState);
      if (conn.connectionState === 'connected' && state.current !== 'idle') {
        if (iceTimeout.current)      { clearTimeout(iceTimeout.current);      iceTimeout.current = null; }
        if (disconnectTimer.current) { clearTimeout(disconnectTimer.current); disconnectTimer.current = null; }
        // Single source of truth for 'connected' — both caller and callee go through here
        setCallState('connected');
        setCallDuration(0);
        if (timer.current) clearInterval(timer.current);
        timer.current = setInterval(() => setCallDuration(d => d + 1), 1000);
        if (timeout.current) { clearTimeout(timeout.current); timeout.current = null; }
      }
      if (conn.connectionState === 'failed') {
        if (state.current !== 'idle') {
          setCallError('Connection failed — both devices may be on restrictive networks. Try again on Wi-Fi.');
          cleanup();
        }
      }
      if (conn.connectionState === 'closed') {
        if (state.current !== 'idle') cleanup();
      }
      if (conn.connectionState === 'disconnected') {
        if (disconnectTimer.current) clearTimeout(disconnectTimer.current);
        disconnectTimer.current = setTimeout(() => {
          if (conn.connectionState === 'disconnected' && state.current !== 'idle') {
            setCallError('Call dropped — network connection was lost.');
            cleanup();
          }
        }, 6000);
      }
    };

    conn.oniceconnectionstatechange = () => {
      console.log('[Call] ICE state:', conn.iceConnectionState);
      if (conn.iceConnectionState === 'failed') {
        try { conn.restartIce(); } catch {}
      }
    };

    return conn;
  }, [myId, cleanup, setRemoteStreamBoth]);

  const handleSignal = useCallback(async (data: any, key: string) => {
    if (!data || !data.t || data.f === myId || !key) return;
    if (seen.current.has(key)) return;

    // Ignore tombstone markers
    if (data._tombstone) return;

    // Ignore signals older than our last cleanup (stale signals from previous session)
    if (data.ts && data.ts < cleanedUpAt.current) return;
    // General staleness guard (60s)
    if (data.ts && Date.now() - data.ts > 60000) return;

    seen.current.add(key);
    console.log('[Call] Signal:', data.t, 'from:', (data.f || '').slice(-6));

    // ── OFFER ────────────────────────────────────────────────────────────────
    if (data.t === 'offer' && state.current === 'idle') {
      room.current = data.room || makeRoomId(myId, data.f);
      peer.current = data.f;
      setIncomingCall({
        callId:     data.room || '',
        callerId:   data.f,
        callerNick: data.nick || 'Someone',
        receiverId: myId,
        type:       'offer',
        data:       data.d,
        timestamp:  data.ts,
        withVideo:  !!data.v,
      });
      setCallState('ringing');

      if (ringTimeout.current) clearTimeout(ringTimeout.current);
      ringTimeout.current = setTimeout(() => {
        if (state.current === 'ringing') {
          console.log('[Call] Ring timeout — auto-dismissing');
          // Write missed call to thread
          const tKey = dmThreadKey(myId, data.f);
          writeSystemMessage(tKey, `📞 Missed call from ${data.nick || 'someone'}`);
          setIncomingCall(null);
          setCallState('idle');
          room.current = '';
          peer.current = '';
        }
      }, RING_TIMEOUT_MS);
    }

    // ── ANSWER ───────────────────────────────────────────────────────────────
    if (data.t === 'answer' && pc.current) {
      try {
        await pc.current.setRemoteDescription(JSON.parse(data.d));
        console.log('[Call] Remote desc set (answer)');
        for (const c of iceBuf.current) {
          try { await pc.current.addIceCandidate(c); } catch {}
        }
        iceBuf.current = [];
      } catch (e) { console.error('[Call] Set answer error:', e); }
    }

    // ── ICE ──────────────────────────────────────────────────────────────────
    if (data.t === 'ice') {
      try {
        const candidate = JSON.parse(data.d);
        if (pc.current?.remoteDescription) {
          await pc.current.addIceCandidate(candidate);
        } else {
          iceBuf.current.push(candidate);
        }
      } catch {}
    }

    // ── END / REJECTED ───────────────────────────────────────────────────────
    if (data.t === 'end' || data.t === 'rejected') cleanup();
  }, [myId, cleanup]);

  // Personal channel listener
  useEffect(() => {
    if (!myId) return;
    console.log('[Call] Listening on personal channel:', myId.slice(-6));
    const mapNode = gun.get('jrtc').get(myId).map();
    mapNode.on((data: any, key: string) => { handleSignal(data, key); });
    return () => { try { mapNode.off(); } catch {} };
  }, [myId, handleSignal]);

  // Shared room channel — uses roomId state (not inCall) so the listener starts the moment the
  // room ID is assigned, before React re-renders for callState. This closes the ICE race window
  // where early ICE candidates arrive before the listener is registered.
  const inCall = callState !== 'idle';
  useEffect(() => {
    if (!roomId || !myId) return;
    console.log('[Call] Listening on room:', roomId.slice(-10));
    const mapNode = gun.get('jrtc').get(roomId).map();
    mapNode.on((data: any, key: string) => { handleSignal(data, key); });
    return () => { try { mapNode.off(); } catch {} };
  }, [roomId, myId, handleSignal]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const startCall = useCallback(async (peerId: string, withVideo: boolean, peerNick?: string) => {
    if (state.current !== 'idle') return;
    try {
      console.log('[Call] Starting call to:', peerId.slice(-6));
      const stream = await getMedia(withVideo);
      const r = makeRoomId(myId, peerId);

      setLocalStreamBoth(stream);
      setCallState('calling');
      setCallPeerNick(peerNick || '');
      room.current     = r;
      peer.current     = peerId;
      peerNickRef.current = peerNick || '';
      seen.current     = new Set();
      setRoomId(r);

      const conn = createPC(stream);
      pc.current = conn;

      const offer = await conn.createOffer();
      await conn.setLocalDescription(offer);

      const sig = `offer_${Date.now()}`;
      gun.get('jrtc').get(peerId).get(sig).put({
        f: myId, nick: myNick, t: 'offer',
        d: JSON.stringify(offer), room: r,
        v: withVideo, ts: Date.now(),
      } as any);

      console.log('[Call] Offer sent to:', peerId.slice(-6));

      // No-answer timeout → write missed call
      timeout.current = setTimeout(() => {
        if (state.current === 'calling') {
          console.log('[Call] No answer — timing out');
          const tKey = dmThreadKey(myId, peerId);
          writeSystemMessage(tKey, `📞 Missed call — no answer`);
          setCallError('No answer. The other person may be offline.');
          cleanup();
        }
      }, CALL_TIMEOUT_MS);

      // ICE connection timeout
      if (iceTimeout.current) clearTimeout(iceTimeout.current);
      iceTimeout.current = setTimeout(() => {
        if (state.current !== 'connected' && state.current !== 'idle') {
          console.log('[Call] ICE timeout');
          setCallError('Could not establish a connection. Check your network and try again.');
          cleanup();
        }
      }, 25000);
    } catch (e: any) {
      console.error('[Call] startCall error:', e);
      setCallError(e?.message || 'Could not start the call.');
      cleanup();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId, myNick, createPC, cleanup, setLocalStreamBoth]);

  const answerCall = useCallback(async () => {
    if (!incomingCall || state.current !== 'ringing') return;
    if (ringTimeout.current) { clearTimeout(ringTimeout.current); ringTimeout.current = null; }
    try {
      console.log('[Call] Answering from:', incomingCall.callerId.slice(-6));
      const stream = await getMedia(incomingCall.withVideo);
      const r = room.current || incomingCall.callId || makeRoomId(myId, incomingCall.callerId);

      setLocalStreamBoth(stream);
      setCallPeerNick(incomingCall.callerNick);
      room.current     = r;
      peer.current     = incomingCall.callerId;
      peerNickRef.current = incomingCall.callerNick;
      setRoomId(r);

      const conn = createPC(stream);
      pc.current = conn;

      await conn.setRemoteDescription(JSON.parse(incomingCall.data));

      for (const c of iceBuf.current) {
        try { await conn.addIceCandidate(c); } catch {}
      }
      iceBuf.current = [];

      const answer = await conn.createAnswer();
      await conn.setLocalDescription(answer);

      // ICE timeout for callee
      if (iceTimeout.current) clearTimeout(iceTimeout.current);
      iceTimeout.current = setTimeout(() => {
        if (state.current !== 'connected' && state.current !== 'idle') {
          console.log('[Call] ICE timeout (callee)');
          setCallError('Could not establish a connection. Check your network and try again.');
          cleanup();
        }
      }, 25000);

      const sig = `answer_${Date.now()}`;
      gun.get('jrtc').get(r).get(sig).put({
        f: myId, t: 'answer', d: JSON.stringify(answer), ts: Date.now(),
      } as any);

      console.log('[Call] Answer sent');
      setIncomingCall(null);
      // ⚠️ Do NOT set 'connected' here — let onconnectionstatechange be the single source of truth
      setCallState('connecting' as CallState);
    } catch (e: any) {
      console.error('[Call] answerCall error:', e);
      setCallError(e?.message || 'Could not answer the call.');
      if (room.current) {
        gun.get('jrtc').get(room.current).get(`rejected_${Date.now()}`).put({
          f: myId, t: 'rejected', ts: Date.now(),
        } as any);
      }
      setIncomingCall(null);
      setCallState('idle');
      if (ringTimeout.current) { clearTimeout(ringTimeout.current); ringTimeout.current = null; }
      room.current = ''; peer.current = '';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingCall, myId, createPC, cleanup, setLocalStreamBoth]);

  const rejectCall = useCallback(() => {
    if (ringTimeout.current) { clearTimeout(ringTimeout.current); ringTimeout.current = null; }
    const callerId  = incomingCall?.callerId  || peer.current;
    const callerNick = incomingCall?.callerNick || '';
    if (room.current) {
      gun.get('jrtc').get(room.current).get(`rejected_${Date.now()}`).put({
        f: myId, t: 'rejected', ts: Date.now(),
      } as any);
    }
    // Write missed call to chat thread
    if (callerId && myId) {
      const tKey = dmThreadKey(myId, callerId);
      writeSystemMessage(tKey, `📞 Missed call from ${callerNick || 'someone'}`);
    }
    setIncomingCall(null);
    setCallState('idle');
    room.current = ''; peer.current = '';
  }, [myId, incomingCall]);

  const endCall = useCallback(() => {
    if (room.current) {
      gun.get('jrtc').get(room.current).get(`end_${Date.now()}`).put({
        f: myId, t: 'end', ts: Date.now(),
      } as any);
    }
    // Write call ended system message
    if (peer.current && myId && state.current === 'connected') {
      const tKey = dmThreadKey(myId, peer.current);
      const mins = Math.floor((timer.current ? callDuration : 0) / 60);
      const secs = (timer.current ? callDuration : 0) % 60;
      const dur = callDuration > 0 ? ` · ${mins}:${String(secs).padStart(2, '0')}` : '';
      writeSystemMessage(tKey, `📞 Call ended${dur}`);
    }
    cleanup();
  }, [myId, callDuration, cleanup]);

  const toggleMute = useCallback(() => {
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsMuted(p => !p);
  }, []);

  const toggleVideo = useCallback(() => {
    localStreamRef.current?.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsVideoOff(p => !p);
  }, []);

  const toggleSpeaker = useCallback(async () => {
    const el = audioElRef.current;
    if (!el) return;
    const next = !isSpeaker;
    setIsSpeaker(next);
    // setSinkId: supported on Chrome/Android. On iOS this is a no-op but we show the button anyway.
    if ((el as any).setSinkId) {
      try {
        // 'speaker' is a non-standard sinkId hint; empty string = default (earpiece on mobile)
        await (el as any).setSinkId(next ? '' : 'communications');
      } catch {
        try { await (el as any).setSinkId(''); } catch {}
      }
    }
  }, [isSpeaker]);

  return {
    callState, localStream, remoteStream,
    isMuted, isVideoOff, isSpeaker, callDuration,
    incomingCall, callPeerNick, callError,
    startCall, answerCall, rejectCall, endCall,
    toggleMute, toggleVideo, toggleSpeaker,
    dismissError: () => setCallError(null),
  };
}
